/**
 * Relationship Bundle Web Component
 * Keeps storefront add-to-cart behavior in sync with the bundle app's
 * variant metafields:
 * - bundles.fulfillment_product swaps the submitted parent variant.
 * - bundles.cart_add_items replaces the submitted parent variant with a
 *   configured list of cart lines.
 */

(function () {
  const CART_ADD_PATH = /\/cart\/add(?:\.js)?(?:[?#]|$)/;
  const instances = new Set();
  let originalFetch = null;

  function normalizeVariantId(value) {
    const text = String(value || "").trim();
    const match = text.match(/ProductVariant\/(\d+)$/);
    return match ? match[1] : text;
  }

  function readQuantity(value, fallback) {
    const parsed = parseInt(String(value || ""), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  function getFetchUrl(input) {
    if (typeof input === "string") return input;
    if (input instanceof URL) return input.toString();
    if (typeof Request !== "undefined" && input instanceof Request) {
      return input.url;
    }
    return "";
  }

  function isCartAddRequest(input) {
    return CART_ADD_PATH.test(getFetchUrl(input));
  }

  function getItemVariantId(item) {
    if (!item) return "";
    return normalizeVariantId(item.id || item.variantId || item.variant_id);
  }

  function getSelectedVariantIdFromForm(form) {
    if (!form) return "";

    const selectedId = form.querySelector('[name="id"]');
    if (!selectedId) return "";

    return normalizeVariantId(selectedId.value);
  }

  function findProductScope(element) {
    if (!element || !element.closest) return null;
    return (
      element.closest('[data-section-type="product"]') ||
      element.closest('[data-section-id]') ||
      element.closest("form") ||
      document
    );
  }

  function findFormForVariant(variantId) {
    const normalizedId = normalizeVariantId(variantId);
    if (!normalizedId) return null;

    const forms = Array.from(document.querySelectorAll('form[action*="/cart/add"]'));
    return (
      forms.find((form) => getSelectedVariantIdFromForm(form) === normalizedId) ||
      null
    );
  }

  function findBundleInScope(scope, variantId) {
    if (!scope || !scope.querySelector) return null;

    const bundles = Array.from(scope.querySelectorAll("relationship-bundle"));
    if (!bundles.length) return null;

    const normalizedId = normalizeVariantId(variantId);
    return (
      bundles.find((bundle) => bundle.matchesVariant(normalizedId)) ||
      (bundles.length === 1 ? bundles[0] : null)
    );
  }

  function findBundleForVariant(variantId) {
    const normalizedId = normalizeVariantId(variantId);
    if (!normalizedId) return null;

    const form = findFormForVariant(normalizedId);
    const scopedBundle = findBundleInScope(findProductScope(form), normalizedId);
    if (scopedBundle) return scopedBundle;

    return (
      Array.from(instances).find(
        (bundle) => bundle.isConnected && bundle.matchesVariant(normalizedId)
      ) || null
    );
  }

  function parseCartAddItems(raw) {
    if (!raw) return [];

    let parsed = raw;
    for (let i = 0; i < 2 && typeof parsed === "string"; i += 1) {
      try {
        parsed = JSON.parse(parsed);
      } catch (error) {
        return [];
      }
    }

    const items = Array.isArray(parsed)
      ? parsed
      : Array.isArray(parsed && parsed.items)
        ? parsed.items
        : [];

    return items
      .map((item) => {
        const id = normalizeVariantId(
          item && (item.variantId || item.variant_gid || item.variant_id || item.id)
        );
        if (!id) return null;

        return {
          id,
          quantity: readQuantity(item && item.quantity, 1),
        };
      })
      .filter(Boolean);
  }

  const LEGACY_HIDDEN_OPTION_KEYS = [
    "_bundle_print_surface",
    "_bundle_flag_base",
    "_bundle_color",
    "_bundle_flag_size",
  ];

  function getHiddenOptionPropertyKey(optionName) {
    const normalizedName = String(optionName || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

    return normalizedName ? `_${normalizedName}` : "";
  }

  function findBundleForm(bundle, form) {
    return (
      form ||
      findFormForVariant(bundle.currentVariantId) ||
      bundle.closest('[data-section-type="product"]')?.querySelector(
        'form[action*="/cart/add"]'
      ) ||
      document.querySelector('form[action*="/cart/add"]')
    );
  }

  function collectOptionProperties(bundle, form) {
    const values = new Map();
    const targetForm = findBundleForm(bundle, form);
    if (!targetForm) return values;

    targetForm.querySelectorAll(".fake-property-input").forEach((input) => {
      const propertyName = String(
        input.getAttribute("data-fake-property") || ""
      ).trim();
      const value = String(input.value || "").trim();

      if (
        !propertyName ||
        !value ||
        propertyName === "SKU" ||
        propertyName.startsWith("_")
      ) {
        return;
      }

      values.set(propertyName, value);
    });

    return values;
  }

  function mergeRelationshipProperties(item, bundle, form) {
    const originalProperties = Object.assign(
      {},
      (item && item.properties) || {}
    );
    const optionProperties = collectOptionProperties(bundle, form);
    const hiddenOptionPropertyKeys = new Set(
      Array.from(optionProperties.keys())
        .map(getHiddenOptionPropertyKey)
        .filter(Boolean)
    );
    const properties = {};
    const title = bundle.getAttribute("main-product-title") || "";
    const url = bundle.getAttribute("main-product-url") || "";
    const customArtwork = bundle.getAttribute("custom-artwork") === "true";

    bundle.captureFakePropertiesForLinkedVariant(form);

    Object.keys(originalProperties).forEach((propertyName) => {
      if (
        propertyName !== "_bundle_variant_options" &&
        propertyName !== "_bundle_product_title" &&
        propertyName !== "_bundle_product_url" &&
        !optionProperties.has(propertyName) &&
        !hiddenOptionPropertyKeys.has(propertyName) &&
        !LEGACY_HIDDEN_OPTION_KEYS.includes(propertyName) &&
        !Object.prototype.hasOwnProperty.call(properties, propertyName)
      ) {
        properties[propertyName] = originalProperties[propertyName];
      }
    });

    optionProperties.forEach((value, optionName) => {
      const propertyKey = getHiddenOptionPropertyKey(optionName);
      if (propertyKey) {
        properties[propertyKey] = value;
      }
    });

    if (title) properties._Title = title;
    if (url) properties._URL = url;
    if (customArtwork) properties._custom_artwork = "true";

    return properties;
  }

  function buildCartAddReplacementItems(originalItem, bundle, form) {
    const baseQuantity = readQuantity(originalItem && originalItem.quantity, 1);
    const cartAddItems = bundle.getCartAddItems();

    if (cartAddItems.length) {
      return cartAddItems.map((item, index) => {
        const replacement = {
          id: item.id,
          quantity: item.quantity * baseQuantity,
        };
        if (index === 0) {
          replacement.properties = mergeRelationshipProperties(
            originalItem,
            bundle,
            form
          );
        }
        return replacement;
      });
    }

    const linkedVariantId = bundle.getLinkedVariantId();
    if (!linkedVariantId) return null;

    return [
      Object.assign({}, originalItem, {
        id: normalizeVariantId(linkedVariantId),
        quantity: baseQuantity,
        properties: mergeRelationshipProperties(originalItem, bundle, form),
      }),
    ];
  }

  function setHeader(options, name, value) {
    if (!options.headers) {
      options.headers = {};
    }

    if (options.headers instanceof Headers) {
      options.headers.set(name, value);
      return;
    }

    if (Array.isArray(options.headers)) {
      const existing = options.headers.find(
        (entry) => String(entry[0]).toLowerCase() === name.toLowerCase()
      );
      if (existing) {
        existing[1] = value;
      } else {
        options.headers.push([name, value]);
      }
      return;
    }

    options.headers[name] = value;
  }

  function rewriteAsJsonItems(options, items) {
    options.body = JSON.stringify({ items });
    setHeader(options, "Content-Type", "application/json");
    setHeader(options, "Accept", "application/json");
  }

  function removeLegacyOptionProperties(body, optionProperties) {
    body.delete("properties[_bundle_variant_options]");
    body.delete("properties[_bundle_product_title]");
    body.delete("properties[_bundle_product_url]");
    body.delete("properties[_Title]");
    body.delete("properties[_URL]");
    optionProperties.forEach((value, optionName) => {
      const propertyKey = getHiddenOptionPropertyKey(optionName);
      body.delete(`properties[${optionName}]`);
      if (propertyKey) {
        body.delete(`properties[${propertyKey}]`);
      }
    });
    LEGACY_HIDDEN_OPTION_KEYS.forEach((propertyName) => {
      body.delete(`properties[${propertyName}]`);
    });
  }

  function transformJsonBody(body, options) {
    if (!body || typeof body !== "object") return false;

    if (Array.isArray(body.items)) {
      const firstItem = body.items[0];
      const bundle = findBundleForVariant(getItemVariantId(firstItem));
      if (!bundle) return false;

      const form = findFormForVariant(getItemVariantId(firstItem));
      const replacementItems = buildCartAddReplacementItems(firstItem, bundle, form);
      if (!replacementItems) return false;

      body.items = replacementItems.concat(body.items.slice(1));
      options.body = JSON.stringify(body);
      return true;
    }

    if (body.id || body.variantId || body.variant_id) {
      const variantId = getItemVariantId(body);
      const bundle = findBundleForVariant(variantId);
      if (!bundle) return false;

      const form = findFormForVariant(variantId);
      const replacementItems = buildCartAddReplacementItems(body, bundle, form);
      if (!replacementItems) return false;

      if (bundle.getCartAddItems().length) {
        body.items = replacementItems;
        delete body.id;
        delete body.variantId;
        delete body.variant_id;
        delete body.quantity;
        delete body.properties;
      } else {
        body.id = replacementItems[0].id;
        delete body.variantId;
        delete body.variant_id;
        body.quantity = replacementItems[0].quantity;
        body.properties = replacementItems[0].properties;
      }

      options.body = JSON.stringify(body);
      return true;
    }

    return false;
  }

  function transformFormDataBody(body, options) {
    const variantId = normalizeVariantId(body.get("id"));
    const bundle = findBundleForVariant(variantId);
    if (!bundle) return false;

    const form = findFormForVariant(variantId);
    const replacementItems = buildCartAddReplacementItems(
      {
        id: variantId,
        quantity: body.get("quantity") || 1,
      },
      bundle,
      form
    );
    if (!replacementItems) return false;

    if (bundle.getCartAddItems().length) {
      rewriteAsJsonItems(options, replacementItems);
      return true;
    }

    body.set("id", replacementItems[0].id);
    removeLegacyOptionProperties(
      body,
      collectOptionProperties(bundle, form)
    );

    const properties = replacementItems[0].properties || {};
    Object.keys(properties).forEach((key) => {
      body.set(`properties[${key}]`, properties[key]);
    });

    return true;
  }

  function transformSearchParamsBody(body, options) {
    const variantId = normalizeVariantId(body.get("id"));
    const bundle = findBundleForVariant(variantId);
    if (!bundle) return false;

    const form = findFormForVariant(variantId);
    const replacementItems = buildCartAddReplacementItems(
      {
        id: variantId,
        quantity: body.get("quantity") || 1,
      },
      bundle,
      form
    );
    if (!replacementItems) return false;

    if (bundle.getCartAddItems().length) {
      rewriteAsJsonItems(options, replacementItems);
      return true;
    }

    body.set("id", replacementItems[0].id);
    removeLegacyOptionProperties(
      body,
      collectOptionProperties(bundle, form)
    );

    const properties = replacementItems[0].properties || {};
    Object.keys(properties).forEach((key) => {
      body.set(`properties[${key}]`, properties[key]);
    });

    options.body = body.toString();
    return true;
  }

  function transformCartAddRequest(options) {
    if (!options || !options.body) return;

    const body = options.body;

    if (typeof FormData !== "undefined" && body instanceof FormData) {
      transformFormDataBody(body, options);
      return;
    }

    if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
      transformSearchParamsBody(body, options);
      return;
    }

    if (typeof body !== "string") return;

    try {
      transformJsonBody(JSON.parse(body), options);
      return;
    } catch (error) {
      if (body.indexOf("=") === -1) return;
    }

    transformSearchParamsBody(new URLSearchParams(body), options);
  }

  function installCartInterception() {
    if (originalFetch || typeof window.fetch !== "function") return;

    originalFetch = window.fetch;
    window.fetch = function (input, options) {
      if (isCartAddRequest(input) && options && options.body) {
        try {
          transformCartAddRequest(options);
        } catch (error) {
          console.error("RelationshipBundle: cart add transform failed:", error);
        }
      }

      return originalFetch.apply(this, arguments);
    };
  }

  class RelationshipBundle extends HTMLElement {
    constructor() {
      super();
      this.cache = new Map();
      this.pendingRequest = null;
      this.currentVariantId = null;
      this.linkedVariantId = null;
      this.cartAddItemsRaw = "";
      this.appManagedMapping = null;
      this.appManagedRequest = 0;
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
    }

    connectedCallback() {
      this.productHandle = this.getAttribute("product-handle");
      this.sectionId = this.getAttribute("section-id");
      this.currentVariantId = this.getAttribute("initial-variant-id");
      this.linkedVariantId = this.getAttribute("linked-variant-id") || null;
      this.cartAddItemsRaw = this.getAttribute("cart-add-items") || "";
      this.mainProductTitle = this.getAttribute("main-product-title") || null;
      this.mainProductUrl = this.getAttribute("main-product-url") || null;
      this.mainVariantOptions = this.getAttribute("main-variant-options") || null;

      instances.add(this);
      document.addEventListener("variant:changed", this.boundHandleVariantChange);
      installCartInterception();
    }

    disconnectedCallback() {
      instances.delete(this);
      document.removeEventListener("variant:changed", this.boundHandleVariantChange);
    }

    captureFakePropertiesForLinkedVariant(form) {
      if (!this.linkedVariantId || !window.fakePropertiesManager) return;

      const manager = window.fakePropertiesManager;
      const targetForm = findBundleForm(this, form);

      if (!targetForm) return;

      const fakeInputs = targetForm.querySelectorAll(".fake-property-input");
      fakeInputs.forEach((input) => {
        const propertyName = input.getAttribute("data-fake-property");
        const value = input.value;

        if (propertyName && value && value.trim() !== "") {
          manager.setProperty(String(this.linkedVariantId), propertyName, value);
        }
      });
    }

    handleVariantChange(event) {
      const eventScope = findProductScope(event.target);
      if (eventScope && eventScope !== document && !eventScope.contains(this)) {
        return;
      }

      const variant = event.detail && event.detail.variant;
      if (!variant || String(variant.id) === String(this.currentVariantId)) return;

      this.currentVariantId = variant.id;

      if (variant.options && Array.isArray(variant.options)) {
        this.mainVariantOptions = variant.options.join(" / ");
        this.setAttribute("main-variant-options", this.mainVariantOptions);
      }

      this.updateLinkedVariant(variant.id);
    }

    async updateLinkedVariant(variantId) {
      if (!this.productHandle || !this.sectionId) return;

      const cacheKey = `${this.productHandle}-${variantId}`;
      if (this.cache.has(cacheKey)) {
        this.applyBundleData(this.cache.get(cacheKey));
        return;
      }

      try {
        const url = `${window.routes.rootUrlWithoutSlash}/products/${this.productHandle}?variant=${variantId}&section_id=${this.sectionId}`;
        let response = await fetch(url);

        if (!response.ok && response.status === 429) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          response = await fetch(url);
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const html = await response.text();
        const data = this.extractBundleData(html);
        this.cache.set(cacheKey, data);
        this.applyBundleData(data);
      } catch (error) {
        console.error("RelationshipBundle: Error fetching linked variant:", error);
        this.applyBundleData({
          linkedVariantId: null,
          cartAddItemsRaw: "",
        });
      }
    }

    extractBundleData(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const bundleElement = doc.querySelector("relationship-bundle");

      return {
        linkedVariantId: bundleElement?.getAttribute("linked-variant-id") || null,
        cartAddItemsRaw: bundleElement?.getAttribute("cart-add-items") || "",
      };
    }

    applyBundleData(data) {
      if (this.appManagedMapping) return;
      this.linkedVariantId = data.linkedVariantId || null;
      this.cartAddItemsRaw = data.cartAddItemsRaw || "";
      this.updateAttribute();
    }

    async setAppManagedMapping(mapping, orderedOptions) {
      this.appManagedMapping = mapping || null;
      const requestId = ++this.appManagedRequest;

      if (Array.isArray(orderedOptions)) {
        this.mainVariantOptions = orderedOptions.join(" / ");
        this.setAttribute("main-variant-options", this.mainVariantOptions);
      }

      if (!this.appManagedMapping) {
        await this.updateLinkedVariant(this.currentVariantId);
        return;
      }

      this.cartAddItemsRaw = JSON.stringify(mapping.cartAddItems || []);
      this.linkedVariantId = null;
      this.updateAttribute();

      const linkedProductHandle = mapping.linkedProductHandle || mapping.sku;
      if (this.getCartAddItems().length || !linkedProductHandle) return;

      try {
        const handle = String(linkedProductHandle)
          .replace(/^https?:\/\/[^/]+\/products\//, "")
          .replace(/^products\//, "")
          .replace(/^\/+|\/+$/g, "");
        const response = await fetch(
          `${window.routes.rootUrlWithoutSlash}/products/${handle}.js`
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const product = await response.json();
        const linkedVariant =
          product.variants?.find((variant) => variant.available) ||
          product.variants?.[0];
        if (
          requestId !== this.appManagedRequest ||
          mapping !== this.appManagedMapping
        ) {
          return;
        }
        this.linkedVariantId = linkedVariant?.id || null;
        this.updateAttribute();
      } catch (error) {
        console.error(
          "RelationshipBundle: Error resolving app-managed relationship:",
          error
        );
      }
    }

    updateAttribute() {
      if (this.linkedVariantId) {
        this.setAttribute("linked-variant-id", this.linkedVariantId);
      } else {
        this.removeAttribute("linked-variant-id");
      }

      if (this.cartAddItemsRaw) {
        this.setAttribute("cart-add-items", this.cartAddItemsRaw);
      } else {
        this.removeAttribute("cart-add-items");
      }
    }

    matchesVariant(variantId) {
      const normalizedId = normalizeVariantId(variantId);
      return (
        normalizedId &&
        (normalizeVariantId(this.currentVariantId) === normalizedId ||
          normalizeVariantId(this.getAttribute("initial-variant-id")) === normalizedId)
      );
    }

    getCartAddItems() {
      return parseCartAddItems(this.cartAddItemsRaw || this.getAttribute("cart-add-items"));
    }

    getLinkedVariantId() {
      return this.linkedVariantId;
    }
  }

  if (!customElements.get("relationship-bundle")) {
    customElements.define("relationship-bundle", RelationshipBundle);
  }
})();
